import React, { useState } from "react";
import cx from "classnames";
import { useDispatch, useSelector } from "react-redux";
import { Redirect, useHistory, useParams } from "react-router-dom";
import { isMobile } from "react-device-detect";
import dayjs from "dayjs";
import { isEmpty } from "lodash";
// eslint-disable-next-line no-restricted-imports
import { Footer } from "../components/Footer/FooterV2";
import stylesV2 from "./AppointmentSuccessV2.module.scss";
import stylesV3 from "./AppointmentSuccessV3.module.scss";
import { useSchedulingABVariant } from "../utils/useSchedulingABVariant";
import { RootState } from "app/store";
import {
  reinitialize,
  selectCalendarLinks,
  selectCreatedAppointment,
} from "features/scheduling/appointmentCreationSlice";
import {
  selectInsuranceData,
  setCurrentInsurance,
  setInsuranceData,
  setIsEditForm as setIsInsuranceEditForm,
  setShowAddFooter,
} from "features/insuranceDataSlice";
import successCircle from "images/success-circle.svg";
import { usePageViewTracking } from "hooks/usePageViewTracking";
import successTick from "images/success-tick.svg";
import profileDefault from "images/profile-default.svg";
import officePlaceholder from "images/office-placeholder.svg";
import officeWhitePlaceholder from "images/office-white-placeholder.svg";
import { CheckInCardNew } from "features/scheduling/components/CheckInCardNew";
import { selectAppointmentData } from "features/appointmentDataSlice";
import { CalendarLinkModal } from "components/CalendarLinkModal";
import * as paths from "features/routing/paths";
import { LayoutV2 } from "features/layout";
import { FooterPortal } from "components/Footer/Footer";
import { DoctorAvatar } from "components/DoctorAvatar";
import { API_DATE_FORMAT } from "api/constants";
import { doctorFullName, doctorDiscountNameVEH } from "utils/doctorFullName";
import { parseStartTimeWithZone } from "features/appointmentTime";
import { Button, ButtonRole } from "components/Button";
import { maskPhone } from "components/MaskedTextInput/maskPhone";
import { ConfirmAboutYou1 } from "features/checkIn/ConfirmAboutYou1";
import { AboutYou1FormData } from "features/checkIn/types";
import { ConfirmInsurance } from "features/checkIn/ConfirmInsurance";
import { saveAdds } from "api/saveAdds";
import { createInsuranceCoverageNewRequest } from "api/createInsuranceCoverageRequest";
import { skipInsuranceCoverageSaveRequest } from "api/skipInsuranceCoverageRequest";
import { getCurrentAppointmentRequest } from "api/getCurrentAppointmentRequest";
import { RetiringLabel } from "features/scheduling/components/RetiringLabel";
import {
  aboutYouSubmitted,
  insuranceSubmitted,
} from "features/checkIn/helpers";
import { Checkin } from "types/checkin";
import {
  canSubmitAppointmentSuccess,
  hasValidInsuranceForm,
} from "features/scheduling/appointmentSuccessValidation";

interface AppointmentReviewPathParams {
  officeId: string;
}

export const AppointmentSuccessNewV2: React.FC = () => {
  const { isV3 } = useSchedulingABVariant();
  const styles = isV3 ? stylesV3 : stylesV2;
  const appointmentData = useSelector((state: RootState) =>
    selectAppointmentData(state)
  );
  const tempappointmentData = appointmentData;
  const history = useHistory();
  const dispatch = useDispatch();
  const [isModalOpen, setModalOpen] = useState(false);
  const [isSubmiting, setSubmiting] = useState(false);
  const [executeOnce, setExecuteOnce] = useState(false);
  const [insChecked, setInsChecked] = useState(false);
  const [addressDATA, setAddressDATA] = useState<AboutYou1FormData>();
  const insuranceFormDataArr = useSelector(selectInsuranceData);
  const createdAppointment = useSelector(selectCreatedAppointment);
  const calendarLinks = useSelector(selectCalendarLinks);
  const routeParams = useParams<AppointmentReviewPathParams>();
  let { officeId } = routeParams;
  if (
    tempappointmentData &&
    !tempappointmentData.doctor &&
    !tempappointmentData.timeSlot
  ) {
    return (
      <Redirect
        to={paths.v2appointmentStart(officeId, dayjs().format(API_DATE_FORMAT))}
      />
    );
  }
  officeId = tempappointmentData.office.id;
  // eslint-disable-next-line react-hooks/rules-of-hooks
  React.useEffect(() => {
    if (!executeOnce && !createdAppointment && !calendarLinks) {
      setExecuteOnce(true);
      history.push(
        paths.v2appointmentStart(officeId, tempappointmentData.lastRequestUrl)
      );
    }
  }, [
    calendarLinks,
    createdAppointment,
    executeOnce,
    history,
    officeId,
    tempappointmentData.lastRequestUrl,
  ]);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  React.useEffect(() => {
    dispatch(setInsuranceData([{}, {}]));
    dispatch(setCurrentInsurance(0));
    dispatch(setIsInsuranceEditForm(false));
    dispatch(setShowAddFooter(false));
  }, [dispatch]);

  const isUnprocessableEntityError = (error: any): boolean => {
    return error?.code === "422" || error?.response?.status === 422;
  };

  const hasSubmittedAboutYouAndInsurance = (checkin?: Checkin): boolean => {
    return aboutYouSubmitted(checkin) && insuranceSubmitted(checkin);
  };

  const loadCurrentCheckin = async (): Promise<Checkin | undefined> => {
    const currentAppointment = await getCurrentAppointmentRequest();
    return currentAppointment?.checkin;
  };

  // Skip duplicate check-in when reschedule keeps the same appointment id and the patient already submitted.
  React.useEffect(() => {
    let isMounted = true;

    const redirectIfAlreadyCheckedIn = async () => {
      try {
        const checkin = await loadCurrentCheckin();
        if (!isMounted) {
          return;
        }

        if (hasSubmittedAboutYouAndInsurance(checkin)) {
          history.replace(paths.portalHome());
        }
      } catch (_error) {
        // Ignore fetch failures here; submit-time logic will still enforce safe behavior.
      }
    };

    redirectIfAlreadyCheckedIn();

    return () => {
      isMounted = false;
    };
  }, [history]);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  React.useEffect(() => {
    return () => {
      dispatch(reinitialize());
    };
  }, [dispatch]);

  const onSubmitNew = async () => {
    setSubmiting(true);
    let isFormValid = false;
    const patientId = createdAppointment?.patientId;
    const appointmentId = createdAppointment?.id;

    if (!patientId) {
      setSubmiting(false);
      return;
    }

    const dummyArr = [];
    if (
      !isEmpty(insuranceFormDataArr[0]) &&
      hasValidInsuranceForm(insuranceFormDataArr[0])
    ) {
      dummyArr.push({
        ...insuranceFormDataArr[0],
        patient_id: patientId,
        appointment_id: appointmentId,
      });
    }
    if (
      !isEmpty(insuranceFormDataArr[1]) &&
      hasValidInsuranceForm(insuranceFormDataArr[1])
    ) {
      dummyArr.push({
        ...insuranceFormDataArr[1],
        patient_id: patientId,
        appointment_id: appointmentId,
      });
    }
    for (const insurance of insuranceFormDataArr) {
      if (!isEmpty(insurance)) {
        isFormValid = hasValidInsuranceForm(insurance) ? true : false;
        if (!isFormValid) break;
      }
    }
    if (!canSubmitAppointmentSuccess({
      aboutYouData: addressDATA,
      insuranceForms: insuranceFormDataArr,
      skipInsurance: insChecked,
    })) {
      setTimeout(() => {
        setSubmiting(false);
      }, 2000);
      return;
    }

    if (!addressDATA) {
      setSubmiting(false);
      return;
    }

    try {
      try {
        await saveAdds({
          ...addressDATA,
          patient_id: patientId,
          appointment_id: appointmentId,
        });
      } catch (error) {
        if (isUnprocessableEntityError(error)) {
            const checkin = await loadCurrentCheckin();

            if (hasSubmittedAboutYouAndInsurance(checkin)) {
            history.push(paths.portalHome());
            return;
          }

            // About You is already submitted for this appointment id, so continue with insurance submission.
            if (aboutYouSubmitted(checkin)) {
              // no-op
            } else {
              throw error;
            }
          } else {
            throw new Error("Unable to save the address");
        }
      }

      if (!insChecked) {
        try {
          await createInsuranceCoverageNewRequest(dummyArr);
        } catch (error) {
          if (isUnprocessableEntityError(error)) {
            const checkin = await loadCurrentCheckin();

            if (hasSubmittedAboutYouAndInsurance(checkin)) {
              history.push(paths.portalHome());
              return;
            }

            if (insuranceSubmitted(checkin)) {
              history.push(paths.portalHome());
              return;
            }
            throw error;
          } else {
            throw new Error("Unable to save the insurance");
          }
        }
      } else {
        try {
          await skipInsuranceCoverageSaveRequest({
            patient_id: patientId,
            appointment_id: appointmentId,
          });
        } catch (error) {
          if (isUnprocessableEntityError(error)) {
            const checkin = await loadCurrentCheckin();

            if (hasSubmittedAboutYouAndInsurance(checkin)) {
              history.push(paths.portalHome());
              return;
            }

            if (insuranceSubmitted(checkin)) {
              history.push(paths.portalHome());
              return;
            }
            throw error;
          } else {
            throw new Error("Unable to save the skip insurance");
          }
        }
      }

      history.push(paths.checkInHome());
    } catch (error) {
      // Keep the user on this screen and allow retry if a non-idempotent API call fails.
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      setSubmiting(false);
    }
  };

  const getAddressValues = (addressData: any) => {
    setAddressDATA(addressData);
  };

  const handleChangeIns = () => {
    setInsChecked(!insChecked);
  };

  const getInsuranceValues = async (_insuranceData: any) => {
    setSubmiting(false);
  };

  const isSubmitDisabled = !canSubmitAppointmentSuccess({
    aboutYouData: addressDATA,
    insuranceForms: insuranceFormDataArr,
    skipInsurance: insChecked,
  });

  const hyphenPlace = isMobile ? "- " : " - ";
  const vehThreeheadshot =
    tempappointmentData.doctor.offersVEH &&
    tempappointmentData.doctor.headshotUrl
      ? tempappointmentData.doctor.headshotUrl.includes("OD_Group_Final_3.png")
      : false;
  const formattedTime = parseStartTimeWithZone(
    tempappointmentData?.timeSlot
  ).format("h:mm A");
  const canTrackSuccessEvent =
    !!createdAppointment?.patientId && !!createdAppointment?.id;
  // eslint-disable-next-line react-hooks/rules-of-hooks
  usePageViewTracking("step-8-appointment-created", {
    doctorId: tempappointmentData?.doctor?.id,
    slot_type: tempappointmentData?.doctor?.offersVEH ? "VEH" : "Traditional",
    patientId: createdAppointment?.patientId,
    patientPhoneNumber: tempappointmentData?.patientDetails?.phoneNumber,
    patientSmsOptIn: tempappointmentData?.patientDetails?.smsOptInNotices,
    appointmentId: createdAppointment?.id,
    appointmentDate: dayjs(tempappointmentData?.timeSlot?.date).format(
      "YYYY-MM-DD"
    ),
    appointmentTime: formattedTime,
    location: tempappointmentData?.office?.id,
    logicalId: tempappointmentData?.office?.logicalId,
  }, {
    shouldTrack: canTrackSuccessEvent,
    dependencies: [createdAppointment?.patientId, createdAppointment?.id],
  });
  const showDiscount =
    dayjs().add(13, "day") > dayjs(tempappointmentData.timeSlot.date);
  const discountVEHDoctor = doctorDiscountNameVEH();
  const doctorName =
    tempappointmentData.doctor.offersVEH &&
    tempappointmentData.office.discountVEH &&
    discountVEHDoctor &&
    showDiscount
      ? doctorDiscountNameVEH()
      : doctorFullName(tempappointmentData.doctor);
  return (
    <>
      <LayoutV2 office={tempappointmentData.office}>
        <div className={styles.containerNew}>
          <div className={styles.header}>
            <div className={styles.headerTopRow}>
              <div className={styles.headerImage}>
                <img
                  src={successCircle}
                  className={styles.imageCircle}
                  alt="circle"
                />
                <img
                  src={successTick}
                  className={styles.imageTick}
                  alt="circle"
                />
              </div>
              <span className={styles.headerText}>
                {isV3 ? "You're Scheduled!" : "YOU'RE SCHEDULED!"}
              </span>
            </div>
            <div className={styles.timeSlotMobile}>
              {dayjs(tempappointmentData.timeSlot.date).format(
                "ddd, MMM D, YYYY"
              )}{" "}
              at {formattedTime}
            </div>
          </div>
          <div className={styles.borderDivider}></div>
          <div className={styles.content}>
            <div>
              <div className={cx(styles.contentBoxV2, styles.doctorPadding)}>
                <div className={styles.contentLeftV2}>
                  <DoctorAvatar
                    doctor={tempappointmentData.doctor}
                    size="small"
                    className={
                      vehThreeheadshot ? styles.avatar3Heads : styles.avatar
                    }
                    callFrom={"successPage"}
                    selectedOffice={tempappointmentData.office}
                  />
                </div>
                <div className={styles.contentRightV2}>
                  <div className={styles.doctorName}>{doctorName}</div>
                  {tempappointmentData.doctor.retiringDate && (
                    <RetiringLabel
                      doctor={tempappointmentData.doctor}
                      calledFrom="successNew"
                    />
                  )}
                  <div className={styles.timeSlot}>
                    {dayjs(tempappointmentData.timeSlot.date).format(
                      "ddd, MMM D, YYYY"
                    )}{" "}
                    at {formattedTime}
                  </div>
                </div>
              </div>
              <div className={styles.borderDivider}></div>
              <div className={cx(styles.contentBoxV2, styles.doctorPadding)}>
                <div className={styles.contentLeftV2}>
                  <img
                    src={isMobile ? officeWhitePlaceholder : officePlaceholder}
                    className={styles.officeImg}
                    alt="Office"
                  />
                </div>
                <div className={styles.contentRightV2}>
                  <div className={styles.officeName}>
                    {tempappointmentData.office.displayName}
                  </div>
                  <div className={styles.officeAddress}>
                    {tempappointmentData.office.address1}, &nbsp;
                    {tempappointmentData.office.city},{" "}
                    {tempappointmentData.office.stateAbbr}&nbsp;
                    {tempappointmentData.office.formattedZipCode} <br />
                    {tempappointmentData.office.formattedPhone}
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.borderDivider}></div>
            <div>
              <div className={cx(styles.contentBoxV2, styles.patientMobile)}>
                <div
                  className={cx(styles.contentLeftV2, styles.patientProfileV2)}
                >
                  <img src={profileDefault} alt="circle" />
                </div>
                <div
                  className={cx(styles.contentRightV2, styles.patientDetails)}
                >
                  <div className={styles.patientName}>
                    {tempappointmentData.patientDetails.firstName}{" "}
                    {tempappointmentData.patientDetails.lastName}
                  </div>
                  <div className={styles.patientInfo}>
                    Date of Birth{hyphenPlace}
                    {dayjs(
                      tempappointmentData.patientDetails.dateOfBirth
                    ).format("MM/DD/YYYY")}
                  </div>
                  <div className={styles.patientInfo}>
                    Phone{hyphenPlace}
                    {maskPhone(tempappointmentData.patientDetails.phoneNumber)}
                  </div>
                  <div className={styles.patientInfo}>
                    Email
                    {hyphenPlace + tempappointmentData.patientDetails.email}
                  </div>
                </div>
              </div>
              <div className={styles.borderDivider}></div>
            </div>
            <div className={isV3 ? styles.customButtonV2 : styles.buttonContainerV2}>
              <Button
                text="Add to Calendar"
                onClick={(ev) => {
                  setModalOpen(true);
                  ev.stopPropagation();
                }}
                role={ButtonRole.Ghost}
                size="large"
              />
            </div>
            <div className={styles.formContainer}>
              <ConfirmAboutYou1
                aboutYou1Data={null}
                getAddressValues={getAddressValues}
                isSubmit={isSubmiting}
                version={2}
                disableProgressGuard={true}
              />
              <div className={styles.insCheck}>
                <input
                  id="success-new-v2-skip-insurance"
                  type="checkbox"
                  checked={insChecked}
                  onChange={handleChangeIns}
                />
                <label htmlFor="success-new-v2-skip-insurance">
                  {" "}
                  I WILL NOT BE USING INSURANCE
                </label>
              </div>
              {!insChecked && (
                <ConfirmInsurance
                  defaultValues={null}
                  isSubmit={isSubmiting}
                  getInsuranceValues={getInsuranceValues}
                  currentPatient={tempappointmentData.patientDetails}
                  version={2}
                  disableStoredFormData={true}
                  alignSsnTooltipToInput={isV3 && !isMobile}
                  disableProgressGuard={true}
                />
              )}
              <CheckInCardNew
                appointment={tempappointmentData.timeSlot}
                onClick={onSubmitNew}
                isSubmit={isSubmiting}
                isDisabled={isSubmitDisabled}
              />
            </div>
          </div>
        </div>

        <div className={styles.line}></div>
        <div className={styles.footer}>
          <div className={styles.wantAnother}>
            <a
              href={
                window.location.origin +
                "/appointments/" +
                tempappointmentData.office.id +
                "/start"
              }
            >
              Need to schedule another exam?
            </a>
          </div>
          <div className={styles.confirmContainer}>
            A confirmation email has been sent to you with a link to your{" "}
            <b>Patient Portal</b>.<br />
            Manage your appointment and check in online through your Patient
            Portal.
            <hr className={styles.footerDividerCustom} />
          </div>
        </div>
        <CalendarLinkModal
          onRequestClose={() => setModalOpen(false)}
          isModalOpen={isModalOpen}
          links={calendarLinks}
        />
        <Footer
          formattedPhone={tempappointmentData.office.formattedPhone}
          useSuccessPageCopy={isV3}
        />
        {<FooterPortal />}
      </LayoutV2>
    </>
  );
};
